import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Category, ImageCrop, Product } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { useToast } from '../../context/ToastContext.js';
import { BigButton } from '../../components/BigButton.js';
import { MoneyInput } from '../../components/MoneyInput.js';
import { Spinner } from '../../components/Spinner.js';
import { StockAdjustDialog } from '../../components/admin/StockAdjustDialog.js';
import { PhotoCropper } from '../../components/admin/PhotoCropper.js';
import { CropPreview } from '../../components/admin/CropPreview.js';
import { sniffImageFile } from '../../lib/imageSniff.js';
import { Page, PageHeader, Field } from '../../components/layout.js';

type CropperMode = 'new' | 'existing';

const CODE_PATTERN = /^[A-Za-z0-9-]*$/;

export function ProductFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [product, setProduct] = useState<Product | null>(null);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [priceCents, setPriceCents] = useState(0);
  const [stockQty, setStockQty] = useState(0);
  const [lowStockThreshold, setLowStockThreshold] = useState(5);
  const [isActive, setIsActive] = useState(true);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [pendingCrop, setPendingCrop] = useState<ImageCrop | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [cropperMode, setCropperMode] = useState<CropperMode | null>(null);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [restockOpen, setRestockOpen] = useState(false);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadCategories = useCallback(async () => {
    try {
      const { categories } = await api.listCategories();
      setCategories(categories);
    } catch {
      // Non-fatal: the category select just falls back to "None" only.
    }
  }, []);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    setLoading(true);
    api
      .listProducts({ includeInactive: true })
      .then(({ products }) => {
        if (cancelled) return;
        const found = products.find((p) => p.id === Number(id));
        if (!found) {
          setLoadError('Product not found');
          return;
        }
        setProduct(found);
        setCode(found.code);
        setName(found.name);
        setCategoryId(found.categoryId ?? '');
        setPriceCents(found.priceCents);
        setLowStockThreshold(found.lowStockThreshold);
        setIsActive(found.isActive);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Could not load product');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, id]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  // Same rules the API enforces on upload (JPEG/PNG/WebP, <=8MB) - checked
  // here too so a bad file is rejected the instant it's picked, with a
  // preview that never appears, rather than only after "Save product"
  // (a broken-image icon plus a "Remove photo" button, as if a photo really
  // had been attached). See the QA report, P2 #10.
  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

  async function handleFileChosen(file: File | null) {
    setImageError(null);
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageError('Image must be JPEG, PNG or WebP');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImageError('Image must be 8MB or smaller');
      return;
    }
    // The MIME/extension checks above trust what the browser/OS reports,
    // which a renamed non-image file (e.g. a text file saved as `.png`)
    // can satisfy. Sniff the file's actual magic bytes before ever
    // previewing it or offering "Remove photo" - see the QA report, P2 #10.
    const sniffed = await sniffImageFile(file);
    if (sniffed === null) {
      setImageError('Not a supported image');
      setImageFile(null);
      return;
    }
    setImageFile(file);
    setPendingCrop(null);
    setRemoveImage(false);
    // The cropper opens automatically on every new/replacement pick, once
    // the object URL below is ready to display.
    setCropperMode('new');
  }

  function handleCropperCancel() {
    if (cropperMode === 'new' && pendingCrop === null) {
      // Cancelling straight out of a fresh pick (never used a crop yet)
      // aborts the pick entirely, falling back to whatever photo state
      // (existing photo, or none) was showing before.
      setImageFile(null);
    }
    setCropperMode(null);
  }

  function handleUseNewCrop(crop: ImageCrop) {
    setPendingCrop(crop);
    setCropperMode(null);
  }

  async function handleUseExistingCrop(crop: ImageCrop) {
    if (!product) return;
    try {
      const updated = await api.updateProductImageCrop(product.id, crop);
      setProduct(updated);
      showToast('Photo position updated', 'success');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not update photo position', 'error');
    } finally {
      setCropperMode(null);
    }
  }

  function handleAdjustExistingPhoto() {
    if (!product) return;
    if (!product.originalUrl) {
      showToast('Please re-upload this photo to adjust its position', 'error');
      return;
    }
    setCropperMode('existing');
  }

  const canSubmit = code.trim().length > 0 && name.trim().length > 0 && !saving;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSaving(true);
    setFormError(null);
    try {
      let productId: number;
      if (isEdit && product) {
        const updated = await api.updateProduct(product.id, {
          name: name.trim(),
          code: code.trim(),
          categoryId: categoryId === '' ? null : categoryId,
          priceCents,
          lowStockThreshold,
          isActive,
        });
        productId = updated.id;
      } else {
        const created = await api.createProduct({
          name: name.trim(),
          code: code.trim(),
          categoryId: categoryId === '' ? null : categoryId,
          priceCents,
          stockQty,
          lowStockThreshold,
          isActive,
        });
        productId = created.id;
      }

      if (imageFile) {
        try {
          await api.uploadProductImage(productId, imageFile, pendingCrop ?? undefined);
        } catch (err) {
          setImageError(err instanceof ApiError ? err.message : 'Could not upload image');
          showToast('Product saved, but the image upload failed', 'error');
          navigate(`/admin/products/${productId}`);
          return;
        }
      } else if (removeImage && product?.imagePath) {
        await api.deleteProductImage(productId);
      }

      showToast(isEdit ? 'Product updated' : 'Product created', 'success');
      navigate('/admin/products');
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
      } else {
        setFormError('Could not save the product');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner label="Loading product" />;
  if (loadError) {
    return (
      <Page>
        <p className="font-body text-base text-danger">{loadError}</p>
      </Page>
    );
  }

  const hasNewPick = imageFile !== null && imagePreview !== null;
  const hasExistingPhoto = !hasNewPick && !removeImage && Boolean(product?.imageUrl);

  return (
    <Page>
      <PageHeader
        title={isEdit ? 'Edit product' : 'Add product'}
        actions={
          isEdit && product ? (
            <>
              <span className="min-h-12 flex items-center font-body text-sm text-muted">Stock: {product.stockQty}</span>
              <button
                type="button"
                onClick={() => setRestockOpen(true)}
                className="min-h-12 px-4 rounded-lg font-heading font-semibold text-sm bg-aqua text-teal-dark"
              >
                Restock / adjust
              </button>
            </>
          ) : undefined
        }
      />

      <form onSubmit={(e) => e.preventDefault()} className="form-width form-stack">
        <Field label="Stock code" htmlFor="product-code" help="Letters, numbers and hyphens only.">
          <input
            id="product-code"
            type="text"
            value={code}
            onChange={(e) => {
              const next = e.target.value.toUpperCase();
              if (CODE_PATTERN.test(next)) setCode(next);
            }}
            maxLength={20}
            placeholder="e.g. KC-ORANGE"
            className="input text-lg font-heading uppercase"
          />
        </Field>

        <Field label="Name" htmlFor="product-name">
          <input
            id="product-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            className="input"
          />
        </Field>

        <Field label="Category" htmlFor="product-category">
          <select
            id="product-category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
            className="input"
          >
            <option value="">None</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        <MoneyInput id="product-price" label="Price" valueCents={priceCents} onChangeCents={(c) => setPriceCents(c ?? 0)} />

        {!isEdit && (
          <Field label="Initial stock" htmlFor="product-stock">
            <input
              id="product-stock"
              type="number"
              inputMode="numeric"
              min={0}
              value={stockQty}
              onChange={(e) => setStockQty(Math.max(0, Number(e.target.value) || 0))}
              className="input text-lg font-heading"
            />
          </Field>
        )}

        <Field label="Low-stock threshold" htmlFor="product-threshold">
          <input
            id="product-threshold"
            type="number"
            inputMode="numeric"
            min={0}
            value={lowStockThreshold}
            onChange={(e) => setLowStockThreshold(Math.max(0, Number(e.target.value) || 0))}
            className="input text-lg font-heading"
          />
        </Field>

        <label className="flex items-center gap-3 font-body font-semibold text-base text-text min-h-12">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-6 w-6 min-h-0 accent-teal shrink-0"
          />
          Active (sellable)
        </label>

        <div className="card card-stack">
          <h2 className="section-title">Photo</h2>

          {hasNewPick && pendingCrop ? (
            <CropPreview src={imagePreview!} crop={pendingCrop} alt="Selected photo preview" />
          ) : hasExistingPhoto ? (
            <div className="h-40 w-40 rounded-lg bg-teal-dark flex items-center justify-center overflow-hidden">
              <img src={product!.thumbUrl ?? product!.imageUrl!} alt="" className="h-full w-full object-cover" />
            </div>
          ) : (
            <div className="h-40 w-40 rounded-lg bg-teal-dark flex items-center justify-center overflow-hidden">
              <span className="text-white font-heading font-semibold text-sm px-2 text-center">{code || 'No photo'}</span>
            </div>
          )}

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            aria-label="Take a photo"
            className="hidden"
            onChange={(e) => void handleFileChosen(e.target.files?.[0] ?? null)}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-label="Choose a photo file"
            className="hidden"
            onChange={(e) => void handleFileChosen(e.target.files?.[0] ?? null)}
          />

          <div className="btn-row">
            {hasNewPick && pendingCrop ? (
              <>
                <BigButton variant="outline" onClick={() => setCropperMode('new')}>
                  Adjust position
                </BigButton>
                <BigButton variant="outline" onClick={() => fileInputRef.current?.click()}>
                  Choose another
                </BigButton>
              </>
            ) : hasExistingPhoto ? (
              <>
                <BigButton variant="outline" onClick={handleAdjustExistingPhoto}>
                  Adjust position
                </BigButton>
                <BigButton variant="outline" onClick={() => fileInputRef.current?.click()}>
                  Replace photo
                </BigButton>
                <BigButton
                  variant="outline"
                  onClick={() => {
                    setImageFile(null);
                    setPendingCrop(null);
                    setRemoveImage(true);
                  }}
                >
                  Remove photo
                </BigButton>
              </>
            ) : (
              <>
                <BigButton variant="outline" onClick={() => cameraInputRef.current?.click()}>
                  Take photo
                </BigButton>
                <BigButton variant="outline" onClick={() => fileInputRef.current?.click()}>
                  Choose file
                </BigButton>
              </>
            )}
          </div>
          {imageError && (
            <p role="alert" className="font-body text-sm text-danger">
              {imageError}
            </p>
          )}
        </div>

        {formError && (
          <p role="alert" className="font-body text-sm text-danger">
            {formError}
          </p>
        )}

        <div className="btn-row">
          <BigButton variant="outline" onClick={() => navigate('/admin/products')}>
            Cancel
          </BigButton>
          <BigButton disabled={!canSubmit} onClick={() => void handleSubmit()}>
            {saving ? 'Saving…' : 'Save product'}
          </BigButton>
        </div>
      </form>

      {restockOpen && product && (
        <StockAdjustDialog
          product={product}
          onClose={() => setRestockOpen(false)}
          onAdjusted={(updated) => {
            setProduct(updated);
            setRestockOpen(false);
          }}
        />
      )}

      {cropperMode === 'new' && imagePreview && (
        <PhotoCropper
          image={imagePreview}
          initialCrop={pendingCrop}
          onCancel={handleCropperCancel}
          onUse={handleUseNewCrop}
        />
      )}
      {cropperMode === 'existing' && product?.originalUrl && (
        <PhotoCropper
          image={product.originalUrl}
          initialCrop={product.imageCrop}
          onCancel={() => setCropperMode(null)}
          onUse={(crop) => void handleUseExistingCrop(crop)}
        />
      )}
    </Page>
  );
}
